import { useState, useEffect, useCallback } from "react";
import {
  Plug, Activity, AlertTriangle, RefreshCw, CheckCircle2, XCircle, Clock,
  Copy, RotateCw, Database, Loader2, Inbox, Eye, Ban, X,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiError, API } from "@/lib/api";

const STATUS_STYLES = {
  success: "bg-emerald-50 text-emerald-700 border-emerald-200",
  possible_duplicate_created: "bg-amber-50 text-amber-700 border-amber-200",
  duplicate_skipped: "bg-slate-50 text-slate-500 border-slate-200",
  partial_success: "bg-orange-50 text-orange-600 border-orange-200",
  deleted_duplicate_detected: "bg-violet-50 text-violet-700 border-violet-200",
  failed: "bg-red-50 text-red-600 border-red-200",
};

const FAILED_LEAD_STATUS_STYLES = {
  pending_retry: "bg-amber-50 text-amber-700 border-amber-200",
  recovered: "bg-emerald-50 text-emerald-700 border-emerald-200",
  ignored: "bg-slate-50 text-slate-500 border-slate-200",
  failed_permanent: "bg-red-50 text-red-600 border-red-200",
};

const fmtTime = (s) => (s ? new Date(s).toLocaleString() : "—");

function StatCard({ label, value, tone = "text-navy", testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-sky/60 bg-mist/40 p-3">
      <p className={`font-sora text-xl font-bold ${tone}`}>{value}</p>
      <p className="text-[11px] text-ink/60">{label}</p>
    </div>
  );
}

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${STATUS_STYLES[status] || "bg-slate-50 text-slate-500 border-slate-200"}`}>
      {status?.replace(/_/g, " ")}
    </span>
  );
}

export default function Integrations() {
  const [health, setHealth] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [failed, setFailed] = useState([]);
  const [failedLeads, setFailedLeads] = useState([]);
  const [logs, setLogs] = useState([]);
  const [sources, setSources] = useState([]);
  const [srcType, setSrcType] = useState("form");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(null);
  const [busyLead, setBusyLead] = useState(null);
  const [rawModal, setRawModal] = useState(null);

  const endpoint = `${API}/integrations/meta/leads`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [h, a, f, fl, l] = await Promise.all([
        api.get("/integrations/meta/health"),
        api.get("/integrations/meta/alerts", { params: { resolved: false } }),
        api.get("/integrations/meta/failed"),
        api.get("/integrations/meta/failed-leads"),
        api.get("/integrations/meta/logs", { params: statusFilter ? { status: statusFilter } : {} }),
      ]);
      setHealth(h.data.data); setAlerts(a.data.data);
      setFailed(f.data.data); setFailedLeads(fl.data.data); setLogs(l.data.data.items);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [statusFilter]);

  const loadSources = useCallback(async () => {
    try {
      const { data } = await api.get("/integrations/meta/sources", { params: { source_type: srcType } });
      setSources(data.data);
    } catch (e) { toast.error(apiError(e)); }
  }, [srcType]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSources(); }, [loadSources]);

  const retry = async (id) => {
    setRetrying(id);
    try {
      const { data } = await api.post(`/integrations/meta/failed/${id}/retry`);
      toast.success(`Retry: ${data.data.action || (data.data.success ? "processed" : "failed")}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setRetrying(null); }
  };

  const resolveAlert = async (id) => {
    try { await api.post(`/integrations/meta/alerts/${id}/resolve`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const retryFailedLead = async (id) => {
    setBusyLead(id);
    try {
      const { data } = await api.post(`/integrations/meta/failed-leads/${id}/retry`);
      const r = data.data;
      if (r.success) toast.success(`Recovered — ${r.action || "processed"}`);
      else toast.error(r.message || r.error || "Could not recover this lead");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusyLead(null); }
  };

  const ignoreFailedLead = async (id) => {
    setBusyLead(id);
    try {
      await api.post(`/integrations/meta/failed-leads/${id}/ignore`);
      toast.success("Marked as ignored");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusyLead(null); }
  };

  const toggleActive = async () => {
    try {
      await api.put("/integrations/meta/settings", { meta_integration_active: !health.active });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const copyEndpoint = () => { navigator.clipboard.writeText(endpoint); toast.success("Endpoint URL copied"); };

  return (
    <div className="space-y-6" data-testid="integrations-page">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-loop/10 text-loop"><Plug className="h-5 w-5" /></span>
        <div>
          <h1 className="font-sora text-2xl font-bold text-navy">Meta Ads</h1>
          <p className="text-sm text-ink/55">Live Meta Lead Ads ingestion via n8n — health, logs, recovery & sources.</p>
        </div>
        <button onClick={load} data-testid="integrations-refresh" className="ml-auto flex items-center gap-2 rounded-xl border border-sky px-3 py-2 text-sm font-medium text-ink/70 hover:border-loop hover:text-loop">
          <RefreshCw className="h-4 w-4" />Refresh
        </button>
      </div>

      {/* Endpoint card */}
      <div className="rounded-2xl border border-sky/70 bg-white p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-ink/50">n8n ingestion endpoint</p>
            <code className="text-sm text-navy" data-testid="integration-endpoint">{endpoint}</code>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={copyEndpoint} data-testid="copy-endpoint-btn" className="flex items-center gap-1.5 rounded-lg border border-sky px-3 py-1.5 text-xs font-medium text-ink/70 hover:border-loop hover:text-loop"><Copy className="h-3.5 w-3.5" />Copy</button>
            {health && (
              <button onClick={toggleActive} data-testid="toggle-active-btn" className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${health.active ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-50 text-slate-500"}`}>
                Integration {health.active ? "Active" : "Paused"}
              </button>
            )}
          </div>
        </div>
        <p className="mt-2 text-xs text-ink/50">Send <code>POST</code> with header <code>Authorization: Bearer &lt;ACQUIRELOOP_API_KEY&gt;</code> and JSON body: <code>idempotency_key, standard_fields, source_metadata, form_answers, raw_meta_payload</code>.</p>
      </div>

      {/* Health */}
      {health && (
        <div className="rounded-2xl border border-sky/70 bg-white p-5" data-testid="integration-health">
          <div className="mb-4 flex items-center gap-2">
            <Activity className="h-5 w-5 text-loop" />
            <h2 className="font-sora text-sm font-semibold text-navy">Health</h2>
            <span data-testid="health-status" className={`ml-2 inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${health.healthy ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-red-200 bg-red-50 text-red-600"}`}>
              {health.healthy ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
              {health.healthy ? "Healthy" : "Needs attention"}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <StatCard testid="health-received-today" label="Received today" value={health.received_today} tone="text-loop" />
            <StatCard testid="health-failures-today" label="Failures today" value={health.failures_today} tone={health.failures_today ? "text-red-500" : "text-navy"} />
            <StatCard testid="health-dupes-today" label="Duplicates skipped" value={health.duplicate_skipped_today} />
            <StatCard testid="health-possible-today" label="Possible dupes" value={health.possible_duplicate_today} tone={health.possible_duplicate_today ? "text-amber-600" : "text-navy"} />
            <StatCard testid="health-failed-open" label="Unprocessed" value={health.failed_unprocessed} tone={health.failed_unprocessed ? "text-orange-500" : "text-navy"} />
            <StatCard testid="health-pending-failed-leads" label="Pending failed leads" value={health.pending_failed_leads ?? 0} tone={health.pending_failed_leads ? "text-red-500" : "text-navy"} />
            <StatCard testid="health-last-received" label="Last lead" value={health.last_lead_received_at ? new Date(health.last_lead_received_at).toLocaleTimeString() : "—"} />
          </div>
          <div className="mt-3 flex flex-wrap gap-4 text-xs text-ink/55">
            <span className="flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />Last success: {fmtTime(health.last_success_at)}</span>
            <span className="flex items-center gap-1"><Inbox className="h-3.5 w-3.5 text-loop" />Last webhook: {fmtTime(health.last_webhook_received_at)}</span>
            <span className="flex items-center gap-1"><XCircle className="h-3.5 w-3.5 text-red-400" />Last failure: {fmtTime(health.last_failed_lead_at || health.last_failed_at)}</span>
          </div>
          {(health.last_failed_lead_error || health.latest_error) && <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600" data-testid="health-latest-error">Latest error: {health.last_failed_lead_error || health.latest_error}</p>}
        </div>
      )}

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-5" data-testid="integration-alerts">
          <h2 className="mb-3 flex items-center gap-2 font-sora text-sm font-semibold text-amber-700"><AlertTriangle className="h-4 w-4" />Alerts ({alerts.length})</h2>
          <div className="space-y-2">
            {alerts.map((a) => (
              <div key={a.id} data-testid={`alert-${a.id}`} className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-white px-3 py-2">
                <span className="text-xs text-ink/70"><strong className="text-amber-700">{a.type?.replace(/_/g, " ")}</strong> — {a.message}</span>
                <button onClick={() => resolveAlert(a.id)} data-testid={`resolve-alert-${a.id}`} className="shrink-0 rounded-lg border border-sky px-2.5 py-1 text-[11px] font-medium text-ink/60 hover:border-loop hover:text-loop">Resolve</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Failed queue */}
      <div className="rounded-2xl border border-sky/70 bg-white p-5" data-testid="failed-queue">
        <h2 className="mb-3 flex items-center gap-2 font-sora text-sm font-semibold text-navy"><RotateCw className="h-4 w-4 text-loop" />Failed queue ({failed.length})</h2>
        {failed.length ? (
          <div className="space-y-2">
            {failed.map((f) => (
              <div key={f.id} data-testid={`failed-row-${f.id}`} className="flex items-center justify-between gap-3 rounded-lg border border-red-100 bg-red-50/40 px-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-xs font-medium text-navy">{f.idempotency_key || f.leadgen_id || "Unknown lead"}</p>
                  <p className="truncate text-[11px] text-red-500">{f.error_message}</p>
                </div>
                <button onClick={() => retry(f.id)} disabled={retrying === f.id} data-testid={`retry-${f.id}`} className="flex shrink-0 items-center gap-1.5 rounded-lg bg-loop px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-loop/90 disabled:opacity-60">
                  {retrying === f.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />}Retry
                </button>
              </div>
            ))}
          </div>
        ) : <p className="py-3 text-center text-sm text-ink/40">No failed ingestions 🎉</p>}
      </div>

      {/* Failed Meta Leads (n8n-side failures) */}
      <div className="rounded-2xl border border-sky/70 bg-white p-5" data-testid="failed-meta-leads">
        <h2 className="mb-1 flex items-center gap-2 font-sora text-sm font-semibold text-navy"><Inbox className="h-4 w-4 text-loop" />Failed Meta Leads ({failedLeads.length})</h2>
        <p className="mb-3 text-xs text-ink/50">Leads n8n could not deliver (Meta fetch, token, mapping or send failures). Retry recovers them safely (same idempotency key — no duplicates).</p>
        {failedLeads.length ? (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead><tr className="border-b border-sky/60 text-left text-xs text-ink/50">
                <th className="px-3 py-2">Lead / leadgen</th><th className="px-3 py-2">Stage</th>
                <th className="px-3 py-2">Error</th><th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Retries</th><th className="px-3 py-2">First failed</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr></thead>
              <tbody className="divide-y divide-sky/40">
                {failedLeads.map((f) => (
                  <tr key={f.id} data-testid={`failed-lead-row-${f.id}`}>
                    <td className="px-3 py-2 text-xs"><span className="font-medium text-navy">{f.leadgen_id || "—"}</span>{f.form_id && <span className="block text-[11px] text-ink/40">form {f.form_id}</span>}</td>
                    <td className="px-3 py-2 text-xs text-ink/70">{f.error_stage || "—"}</td>
                    <td className="px-3 py-2 text-[11px] text-red-500 max-w-[220px] truncate" title={f.error_message}>{f.error_message || "—"}</td>
                    <td className="px-3 py-2"><span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${FAILED_LEAD_STATUS_STYLES[f.status] || "bg-slate-50 text-slate-500 border-slate-200"}`}>{f.status?.replace(/_/g, " ")}</span></td>
                    <td className="px-3 py-2 text-xs text-ink/60">{f.retry_count ?? 0}</td>
                    <td className="px-3 py-2 text-[11px] text-ink/50">{fmtTime(f.first_failed_at)}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1.5">
                        <button onClick={() => setRawModal(f)} data-testid={`view-raw-${f.id}`} title="View raw payload" className="rounded-lg border border-sky p-1.5 text-ink/60 hover:border-loop hover:text-loop"><Eye className="h-3.5 w-3.5" /></button>
                        {f.status !== "recovered" && (
                          <button onClick={() => retryFailedLead(f.id)} disabled={busyLead === f.id} data-testid={`retry-lead-${f.id}`} className="flex items-center gap-1 rounded-lg bg-loop px-2.5 py-1.5 text-[11px] font-semibold text-white hover:bg-loop/90 disabled:opacity-60">
                            {busyLead === f.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />}Retry Now
                          </button>
                        )}
                        {f.status === "pending_retry" && (
                          <button onClick={() => ignoreFailedLead(f.id)} disabled={busyLead === f.id} data-testid={`ignore-lead-${f.id}`} title="Mark ignored" className="flex items-center gap-1 rounded-lg border border-sky px-2.5 py-1.5 text-[11px] font-medium text-ink/60 hover:border-red-300 hover:text-red-500"><Ban className="h-3.5 w-3.5" />Ignore</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="py-3 text-center text-sm text-ink/40">No failed Meta leads from n8n 🎉</p>}
      </div>

      {/* Ingestion logs */}
      <div className="rounded-2xl border border-sky/70 bg-white p-5" data-testid="ingestion-logs">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 className="flex items-center gap-2 font-sora text-sm font-semibold text-navy"><Clock className="h-4 w-4 text-loop" />Ingestion logs</h2>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="log-status-filter" className="rounded-lg border border-sky px-2 py-1 text-xs outline-none focus:border-loop">
            <option value="">All statuses</option>
            {Object.keys(STATUS_STYLES).map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead><tr className="border-b border-sky/60 text-left text-xs text-ink/50">
              <th className="px-3 py-2">Time</th><th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Form / Campaign</th><th className="px-3 py-2">Idempotency</th>
              <th className="px-3 py-2">Method</th>
            </tr></thead>
            <tbody className="divide-y divide-sky/40">
              {logs.map((l) => (
                <tr key={l.id} data-testid={`log-row-${l.id}`}>
                  <td className="px-3 py-2 text-xs text-ink/60">{fmtTime(l.created_at)}</td>
                  <td className="px-3 py-2"><StatusBadge status={l.status} /></td>
                  <td className="px-3 py-2 text-xs text-ink/70">{l.form_name || l.campaign_name || "—"}</td>
                  <td className="px-3 py-2 text-[11px] text-ink/50">{l.idempotency_key || "—"}</td>
                  <td className="px-3 py-2 text-xs text-ink/70">{l.assignment_method || "—"}</td>
                </tr>
              ))}
              {!logs.length && <tr><td colSpan={5} className="py-4 text-center text-sm text-ink/40">{loading ? "Loading…" : "No logs yet."}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Source registry */}
      <div className="rounded-2xl border border-sky/70 bg-white p-5" data-testid="source-registry">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 className="flex items-center gap-2 font-sora text-sm font-semibold text-navy"><Database className="h-4 w-4 text-loop" />Source registry</h2>
          <div className="flex gap-1">
            {["form", "campaign", "adset", "ad"].map((t) => (
              <button key={t} onClick={() => setSrcType(t)} data-testid={`src-tab-${t}`} className={`rounded-lg px-2.5 py-1 text-xs font-semibold capitalize ${srcType === t ? "bg-loop text-white" : "text-ink/60 hover:text-loop"}`}>{t}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {sources.map((s) => (
            <div key={s.id} data-testid={`source-${s.id}`} className="rounded-lg border border-sky/60 bg-mist/30 px-3 py-2">
              <p className="truncate text-sm font-medium text-navy">{s.source_name || "(no name)"}</p>
              <p className="text-[11px] text-ink/50">ID: {s.source_id || "—"} · {s.lead_count || 0} lead(s)</p>
            </div>
          ))}
          {!sources.length && <p className="col-span-full py-3 text-center text-sm text-ink/40">No {srcType}s seen yet.</p>}
        </div>
      </div>

      {/* Raw payload modal */}
      {rawModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="raw-payload-modal" onClick={() => setRawModal(null)}>
          <div className="max-h-[80vh] w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-sky/60 px-5 py-3">
              <h3 className="font-sora text-sm font-semibold text-navy">Raw payload · {rawModal.leadgen_id || "lead"}</h3>
              <button onClick={() => setRawModal(null)} data-testid="close-raw-modal" className="rounded-lg p-1 text-ink/50 hover:bg-mist"><X className="h-4 w-4" /></button>
            </div>
            <div className="max-h-[68vh] overflow-auto px-5 py-4">
              <div className="mb-3 grid grid-cols-2 gap-2 text-xs">
                <p><span className="text-ink/50">Stage:</span> <b className="text-navy">{rawModal.error_stage || "—"}</b></p>
                <p><span className="text-ink/50">Status:</span> <b className="text-navy">{rawModal.status?.replace(/_/g, " ")}</b></p>
                <p className="col-span-2"><span className="text-ink/50">Error:</span> <span className="text-red-500">{rawModal.error_message || "—"}</span></p>
                {rawModal.meta_error_response && <p className="col-span-2"><span className="text-ink/50">Meta error:</span> <span className="text-red-500">{typeof rawModal.meta_error_response === "string" ? rawModal.meta_error_response : JSON.stringify(rawModal.meta_error_response)}</span></p>}
              </div>
              <pre className="rounded-lg bg-mist/60 p-3 text-[11px] text-ink/80 whitespace-pre-wrap break-all">{JSON.stringify(rawModal.raw_webhook_payload ?? {}, null, 2)}</pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
